/* Search drawer (sections/search-drawer.liquid).

   The rule this file follows is the cart's: the server owns what is shown. Typing asks Shopify
   for /search/suggest?q=…&section_id=<predictive-search>, which comes back as markup Liquid
   rendered — prices in the shop's own money format, titles already escaped. This file swaps that
   markup in and does nothing else with it.

   Progressive enhancement, in order of what survives:
   - No JavaScript: the magnifier is a link to /search, and the field inside the drawer is a real
     form that submits there. The drawer is never opened.
   - This file present: the magnifier opens the drawer, and results arrive under the field as you
     type. Enter still goes to the full results page. */

const DRAWER = '[data-search-drawer]';
const routes = () => window.Munkwell?.routes ?? {};
const overlay = () => window.Munkwell?.overlay ?? null;

const drawer = () => document.querySelector(DRAWER);

/* Helix answers on the third character; below that the list is noise. */
const MIN_LENGTH = 2;
const DEBOUNCE_MS = 250;

let timer = 0;
let inFlight = null;
let lastTerms = '';

async function fetchResults(root, terms) {
  const results = root.querySelector('[data-search-results]');
  if (!results) return;

  const section = root.dataset.searchSection || 'predictive-search';
  const url = new URL(`${routes().predictiveSearch || '/search/suggest'}`, window.location.origin);
  url.searchParams.set('q', terms);
  url.searchParams.set('section_id', section);
  url.searchParams.set('resources[type]', 'product,query');
  url.searchParams.set('resources[limit]', '6');

  /* A slower answer must never overwrite a faster one that was typed later. */
  inFlight?.abort();
  const controller = new AbortController();
  inFlight = controller;

  root.classList.add('is-busy');
  try {
    const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(response.statusText);
    const html = await response.text();
    if (controller.signal.aborted) return;

    /* Shopify wraps the section in its own element; only what is inside it belongs in the drawer. */
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('.shopify-section') ?? parsed.body;
    results.innerHTML = fresh.innerHTML.trim();
  } catch (error) {
    if (error.name === 'AbortError') return;
    /* The field still works as a form: Enter goes to the full results page, which is the answer
       this drawer was only ever shortening. */
    results.innerHTML = '';
  } finally {
    if (inFlight === controller) {
      inFlight = null;
      root.classList.remove('is-busy');
    }
  }
}

function clear(root) {
  const results = root.querySelector('[data-search-results]');
  if (results) results.innerHTML = '';
  root.querySelector('[data-search-clear]')?.setAttribute('hidden', '');
  lastTerms = '';
}

function onInput(root, input) {
  const terms = input.value.trim();
  root.querySelector('[data-search-clear]')?.toggleAttribute('hidden', terms.length === 0);

  clearTimeout(timer);
  if (terms.length < MIN_LENGTH) {
    inFlight?.abort();
    const results = root.querySelector('[data-search-results]');
    if (results) results.innerHTML = '';
    lastTerms = '';
    return;
  }
  if (terms === lastTerms) return;

  timer = setTimeout(() => {
    lastTerms = terms;
    fetchResults(root, terms);
  }, DEBOUNCE_MS);
}

document.addEventListener('input', (event) => {
  const input = event.target instanceof Element ? event.target.closest('[data-search-input]') : null;
  const root = input?.closest(DRAWER);
  if (!root) return;
  onInput(root, input);
});

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-search-clear]') : null;
  const root = button?.closest(DRAWER);
  if (!root) return;
  const input = root.querySelector('[data-search-input]');
  if (input) {
    input.value = '';
    input.focus();
  }
  clear(root);
});

/* ---------- Opening the drawer ---------- */

/* The magnifier stays a link to /search in the markup; this upgrades it in place, so a middle
   click or ⌘-click still opens the search page in a tab, as a link should. Same treatment the
   cart icon gets in assets/cart.js. */
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!link) return;

  const searchUrl = routes().search || '/search';
  const path = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
  if (path !== searchUrl.replace(/\/$/, '')) return;

  const panel = drawer();
  if (!panel?.id || !overlay()?.open(panel.id, link)) return;
  event.preventDefault();

  /* The field is the only thing anyone opened this for. */
  requestAnimationFrame(() => panel.querySelector('[data-search-input]')?.focus());
});
